/**
 * @file SupabaseGate.jsx
 * @location src/integrations/supabase/SupabaseGate.jsx
 * @role 인증 게이트 — main.jsx 에서만 마운트 (테스트는 import 안 함 → supabase 미로드).
 *
 *  AppProvider 하위에서 raw 스토어를 받아 Supabase 브리지를 얹고,
 *  phase 에 따라 LoginScreen / JoinScreen / (브리지된 Ctx 로 감싼) children 을 렌더.
 *  ready 단계에서만 children(App) 이 하이드레이션된 상태 + 동기화 act 를 받는다.
 */
import { useState, useEffect } from 'react';
import { Ctx, useApp } from '../../store/AppContext.jsx';
import { parseInviteParam, peekInvite, stashInvite, clearInvite } from '../../utils/inviteLink.js';
import { useSupabaseStore } from './useSupabaseStore.js';
import { LandingScreen } from '../../components/landing/LandingScreen.jsx';
import { LoginScreen } from '../../components/auth/LoginScreen.jsx';
import { JoinScreen } from '../../components/auth/JoinScreen.jsx';
import { NewPasswordScreen } from '../../components/auth/NewPasswordScreen.jsx';
import { RestoreAccountModal } from '../../components/auth/RestoreAccountModal.jsx';
import { LoadingSpinner } from '../../components/ui/sharedui/feedback.jsx';
import { restoreAccount } from './api/account.js';
import '../../components/auth/auth.css';

export function SupabaseGate({ children }) {
  const store = useApp();
  const bridged = useSupabaseStore(store);
  // 비로그인 진입: 랜딩(LP) → 로그인/신규가입 전환. ('landing' | 'login' | 'signup')
  const [authView, setAuthView] = useState('landing');
  // signInWithGoogle 는 bridged.auth 에 그대로 있으나, 베타에선 LoginScreen 에 미전달 → 구글 버튼 숨김.
  // 한국 전용 — 다음 단계로 카카오 로그인 추가 예정(그때 소셜 버튼 재노출).
  const { phase, email, passwordRecovery, updatePassword, exitPasswordRecovery, signInWithPassword, signUpWithPassword, sendPasswordReset, signOut, onCreateTeam, onAcceptInvite } = bridged.auth;

  // 초대 링크(?invite=)로 진입 → 코드 보관(OAuth 리다이렉트 가로지르기) + URL 정리.
  useEffect(() => {
    const code = parseInviteParam(typeof location !== 'undefined' ? location.search : '');
    if (code) {
      stashInvite(code);
      try { history.replaceState(null, '', location.pathname); } catch { /* 무음 */ }
    }
  }, []);
  const pendingInvite = peekInvite();

  if (phase === 'loading') {
    return (
      <div className="auth-screen">
        <div className="suspense-loading" role="status" aria-live="polite">
          <LoadingSpinner size={20} /> 불러오기중…
        </div>
      </div>
    );
  }
  if (passwordRecovery) {
    return <NewPasswordScreen onUpdate={updatePassword} onExit={exitPasswordRecovery} />;
  }
  if (phase === 'signin') {
    if (authView === 'landing' && !pendingInvite) {
      return (
        <LandingScreen
          onStart={() => setAuthView('signup')}
          onLogin={() => setAuthView('login')}
        />
      );
    }
    return (
      <LoginScreen
        initialSignup={authView === 'signup' || !!pendingInvite}
        onBack={() => setAuthView('landing')}
        onSignInWithPassword={signInWithPassword}
        onSignUpWithPassword={signUpWithPassword}
        onSendPasswordReset={sendPasswordReset}
      />
    );
  }
  if (phase === 'join') {
    return (
      <JoinScreen
        email={email}
        initialCode={pendingInvite ?? ''}
        onCreateTeam={onCreateTeam}
        onAcceptInvite={async (args) => { await onAcceptInvite(args); clearInvite(); }}
        onSignOut={async () => { clearInvite(); await signOut(); }}
      />
    );
  }
  // 30일 grace 안 로그인 — 복원/사인아웃 분기. ready 이후만 (deletedScope 는 hydrate 결과)
  if (phase === 'ready' && (bridged.deletedScope === 'team' || bridged.deletedScope === 'member')) {
    return (
      <RestoreAccountModal
        scope={bridged.deletedScope}
        onRestore={async () => {
          await restoreAccount();
          await bridged.refetchWorkspace?.();
        }}
        onCancel={async () => { await signOut(); }}
      />
    );
  }

  // ready — 브리지된 값(하이드레이션 상태 + 동기화 act)으로 Ctx 재공급
  return <Ctx.Provider value={bridged}>{children}</Ctx.Provider>;
}
